import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BookOpen, Check, Image as ImageIcon } from 'lucide-react';
import type { BuyGuideToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useBuyGuidesList, useBuyGuideMutations } from '~/hooks/use-buy-guides';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Buy guides (Οδηγοί Αγοράς) list — image + name + colour + categories + visible + sort; standalone editor on add/edit. */
export function BuyGuidesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useBuyGuidesList();
	const { remove } = useBuyGuideMutations();

	const [deleteRow, setDeleteRow] = useState<BuyGuideToken | null>(null);

	const columns = useMemo<SystemGridColumn<BuyGuideToken>[]>(
		() => [
			{
				key: 'image',
				header: t('catalog.buyGuides.columns.image'),
				width: '70px',
				toggleable: false,
				render: r =>
					r.img1 ? (
						<img src={r.img1} alt={r.nm ?? ''} className="h-9 w-9 rounded border border-border object-contain" />
					) : (
						<div className="flex h-9 w-9 items-center justify-center rounded border border-border bg-elevated">
							<ImageIcon size={14} className="text-foreground-disabled" />
						</div>
					)
			},
			{
				key: 'name',
				header: t('catalog.buyGuides.columns.name'),
				width: 'minmax(0,1.4fr)',
				sortValue: r => r.nm ?? '',
				exportValue: r => r.nm ?? '',
				render: r => (
					<span className="truncate text-sm font-medium text-foreground" title={r.nm ?? ''}>
						{r.nm || '—'}
					</span>
				)
			},
			{
				key: 'background',
				header: t('catalog.buyGuides.columns.background'),
				width: '140px',
				defaultVisible: false,
				sortValue: r => r.bgc ?? '',
				exportValue: r => r.bgc ?? '',
				render: r =>
					r.bgc ? (
						<span className="flex items-center gap-2">
							<span className="h-4 w-4 rounded border border-border" style={{ backgroundColor: r.bgc }} />
							<span className="font-mono text-xs text-foreground-muted">{r.bgc}</span>
						</span>
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'categories',
				header: t('catalog.buyGuides.columns.categories'),
				width: '110px',
				align: 'right',
				sortValue: r => r.catlst?.length ?? 0,
				exportValue: r => r.catlst?.length ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.catlst?.length ?? 0}</span>
			},
			{
				key: 'visible',
				header: t('catalog.buyGuides.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: r => (r.iv ? 1 : 0),
				exportValue: r => (r.iv ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.buyGuides.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: r => r.si ?? 0,
				exportValue: r => r.si ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.si ?? 0}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<BuyGuideToken>
				gridId="buy-guides"
				title={t('catalog.buyGuides.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.nm}
				columns={columns}
				searchPredicate={(r, q) => (r.nm ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<BookOpen size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.buyGuides.add')}
				onAdd={() => navigate('/products/buy-guides/create')}
				onEdit={r => navigate(`/products/buy-guides/${r.id}`)}
				onDelete={setDeleteRow}
				exportFilename="buy-guides"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.buyGuides.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.buyGuides.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
